import type { FC } from 'react'

import { Input } from '@skytech/ui'
import type { FieldProps } from 'formik'
import { Field, useFormikContext } from 'formik'
import AccountPlusOutlineIcon from 'mdi-react/AccountPlusOutlineIcon'
import CalendarStarOutlineIcon from 'mdi-react/CalendarStarOutlineIcon'
import PackageDownIcon from 'mdi-react/PackageDownIcon'
import { twJoin } from 'tailwind-merge'

import { useTranslation } from '@/app/translations/translations-hooks'
import { useIsThreeStepIT } from '@/hooks/access'

import { Reminders } from './Reminders'
import { TimelineItem } from './TimelineItem'

import type { LeasingSettingsFormFields } from '..'

export const Timeline: FC<{
  lockFields?: string[]
  isDisabled?: boolean
}> = ({ lockFields, isDisabled = false }) => {
  const translate = useTranslation()
  const isThreeStepIT = useIsThreeStepIT()
  const { values } = useFormikContext<LeasingSettingsFormFields>()

  const lockOpenOffset = lockFields?.includes('endofleaseDefaultEmployeeSelectionOpenOffset')
  const lockAssetReturnOffset = lockFields?.includes('endofleaseDefaultAssetReturnOffset')

  const showEmployeeSelectionOpen = !lockOpenOffset && !isThreeStepIT
  const showAssetReturn = !lockAssetReturnOffset && !isThreeStepIT

  return (
    <div>
      <Field name="employeeStartOffset">
        {({ field, meta }: FieldProps) => (
          <TimelineItem
            title={translate('Employee heads-up')}
            description={
              isThreeStepIT
                ? [translate('days before selection opens')]
                : [translate('days before lease end')]
            }
            icon={<AccountPlusOutlineIcon size={16} />}
            meta={meta}
            isRightError={true}
            isActive={values.endOfLeaseIsActive && Boolean(field.value)}
          >
            <Input
              id="employeeStartOffset"
              className="w-24 p-2"
              type="number"
              isDisabled={isDisabled || !values.endOfLeaseIsActive}
              min="0"
              max="90"
              touched={meta.touched}
              error={meta.error ? ' ' : undefined}
              errorTextClassName="m-0.5"
              {...field}
              value={field.value ?? ''}
            />
          </TimelineItem>
        )}
      </Field>

      {/* Only for NON-3S case */}
      {showEmployeeSelectionOpen && (
        <Field name="employeeSelectionOpenOffset">
          {({ field, meta }: FieldProps) => (
            <TimelineItem
              title={translate('Shop opens*')}
              description={[translate('days before lease end')]}
              icon={<CalendarStarOutlineIcon size={16} />}
              meta={meta}
              isRightError={true}
              isActive={values.endOfLeaseIsActive && Boolean(field.value)}
            >
              <Input
                id="employeeSelectionOpenOffset"
                className="w-24 p-2"
                type="number"
                min="14"
                max="365"
                isDisabled={isDisabled || !values.endOfLeaseIsActive}
                touched={meta.touched}
                error={meta.error ? ' ' : undefined}
                errorTextClassName="m-0.5"
                {...field}
                value={field.value ?? ''}
              />
            </TimelineItem>
          )}
        </Field>
      )}

      <div className="relative">
        <Line isTop />
        <TimelineItem
          title={translate('Employee reminder settings')}
          description={
            isThreeStepIT
              ? [translate('days before employee selection closes')]
              : [translate('days before lease end')]
          }
          wrapperClassName="sm:pb-0 pb-0"
        />

        <Reminders isDisabled={isDisabled} />

        {showAssetReturn && <Line isBottom />}
      </div>

      {/* Only for NON-3S case */}
      {showAssetReturn && (
        <Field name="assetReturnOffset">
          {({ field, meta }: FieldProps) => (
            <TimelineItem
              title={translate('Asset return cut-off*')}
              description={[
                `${translate('Asset still marked "In use" this many days before the lease end date will automatically move to "For return" and start the return reminder process.')}`,
              ]}
              icon={<PackageDownIcon size={16} />}
              meta={meta}
              isRightError={true}
              isActive={values.endOfLeaseIsActive && Boolean(field.value)}
            >
              <Input
                id="assetReturnOffset"
                className="w-24 p-2"
                type="number"
                min="1"
                max="365"
                isDisabled={isDisabled || !values.endOfLeaseIsActive}
                touched={meta.touched}
                error={meta.error ? ' ' : undefined}
                errorTextClassName="m-0.5"
                {...field}
                value={field.value ?? ''}
              />
            </TimelineItem>
          )}
        </Field>
      )}
    </div>
  )
}

export const Line: FC<{ isTop?: boolean; isBottom?: boolean }> = ({
  isTop = false,
  isBottom = false,
}) => (
  <div
    className={twJoin(
      'ml-8 h-0.5 w-[calc(100%-2.75rem)] bg-gray-300 opacity-50 sm:ml-11',
      isTop && 'top-0',
      isBottom && 'bottom-0'
    )}
  />
)
